import type { WorkflowSummary } from '../workflow/use-workflow-summaries';
import { formatLastRun } from '../workflow/use-workflow-summaries';
import { humanReadableSchedule } from '../trigger/schedule-utils';
// Single source of truth for run-status colours — the Workflows list owns it.
import { runStatusDotClass } from '../workflow/workflow-list-row';
import {
  isVirtualChatsItem,
  type FolderContentsListItem,
} from './folder-contents-list-items';

/**
 * The one-line secondary signal a row/card shows under its name.
 *
 * `run` carries a status dot; `location` is the folder an item is filed in and
 * renders with a folder glyph; `text` is plain.
 */
export type LibraryItemSummary =
  | { kind: 'text'; text: string }
  | { kind: 'run'; text: string; dotClass: string }
  | { kind: 'location'; text: string };

function nonEmpty(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/**
 * Best available one-liner for an item, so every type says *something* useful
 * rather than only agents and workflows carrying a signal.
 *
 * Only agents and documents have a real `description` field; the rest use
 * whatever their payload already provides (a folder's item count, a workflow's
 * run health, a trigger's schedule, a dataset's source file). Anything with
 * nothing type-specific falls back to where it's filed.
 *
 * Shared by {@link LibraryRow} and {@link LibraryCard} so the two views can't
 * drift apart.
 */
export function getLibraryItemSummary(params: {
  item: FolderContentsListItem;
  workflowSummaries: Map<string, WorkflowSummary>;
  /** Folder path/name this item is filed in, when worth showing. */
  folderLabel: string | null;
  /**
   * Whether agent/document descriptions may be used. Grid cards have the room
   * for a two-line summary; a list row has a single short subheading, where a
   * description would crowd out the item's location. Defaults to off.
   */
  includeDescription?: boolean;
}): LibraryItemSummary | null {
  const {
    item,
    workflowSummaries,
    folderLabel,
    includeDescription = false,
  } = params;

  if (isVirtualChatsItem(item)) {
    return { kind: 'text', text: `${item.data.count} chats` };
  }

  const location: LibraryItemSummary | null =
    folderLabel !== null && folderLabel !== ''
      ? { kind: 'location', text: folderLabel }
      : null;

  switch (item.type) {
    case 'folder': {
      // The rows the folder discloses, which is the number someone checking it
      // can count. Chats are excluded from every folder-contents fetch and
      // arrive as ONE collapsed "Chats" row however many there are, so they add
      // one to the count when the folder surfaces any, and nothing when it does
      // not. Counting chats individually made a folder holding nine artifacts
      // and an org's worth of run chats read "61052 items" over a list of nine;
      // ignoring them entirely made a folder read "7 items" over eight rows.
      const listed = item.data.childrenCount;
      if (listed === undefined) {
        return location;
      }
      const count = listed + ((item.data.chatCount ?? 0) > 0 ? 1 : 0);
      return {
        kind: 'text',
        text: `${count} ${count === 1 ? 'item' : 'items'}`,
      };
    }
    case 'agent':
    case 'document': {
      // The only two types with a real description. Same field the workflow
      // agent nodes render (see `agentBodyDescription`). Grid-only — see
      // `includeDescription`.
      if (!includeDescription) {
        return location;
      }
      const description = nonEmpty(item.data.description);
      return description !== null
        ? { kind: 'text', text: description }
        : location;
    }
    case 'workflow': {
      const summary = workflowSummaries.get(item.data.id);
      const lastRunAt = summary?.lastRunAt ?? null;
      return {
        kind: 'run',
        text:
          lastRunAt === null
            ? 'Never run'
            : `Last run ${formatLastRun(lastRunAt)}`,
        dotClass: runStatusDotClass(summary?.lastRunStatus ?? null),
      };
    }
    case 'workflow_trigger': {
      // Schedule is the defining fact about a trigger; the bound agent says
      // what it runs. `humanReadableSchedule` falls back to the raw cron for
      // expressions it can't phrase.
      const schedule = nonEmpty(item.data.cronSchedule);
      const agentTitle = nonEmpty(item.data.agentTitle);
      const parts = [
        schedule !== null ? humanReadableSchedule(schedule) : null,
        agentTitle,
      ].filter((part): part is string => part !== null);
      return parts.length > 0
        ? { kind: 'text', text: parts.join(' · ') }
        : location;
    }
    case 'dataset': {
      const filename = nonEmpty(item.data.originalFilename);
      return filename !== null ? { kind: 'text', text: filename } : location;
    }
    default:
      return location;
  }
}
